import { supabase } from './supabase';
import { SubscriptionPlan, GemPackage } from './ehPrices';
import { getEmailWithoutAlias } from './utils';

// Declare the Atlos global type
declare global {
  interface Window {
    atlos?: {
      Pay: (options: AtlosPayOptions) => void;
    };
  }
}

// Atlos Payment types
interface AtlosPayOptions {
  merchantId: string;
  orderId: string;
  orderAmount: number;
  orderCurrency: string;
  userEmail?: string;
  onSuccess?: (data: string) => Promise<void>;
  onCanceled?: () => void;
  onCompleted?: () => void;
  theme?: 'light' | 'dark';
}

export interface CheckPaymentResponse {
  success: boolean;
  // success: true
  gemBalance?: number;
  subscription?: string;
  subscriptionPlan?: string;
  subscriptionExpDate?: string;
  // success: false
  error?: string;
}

/**
 * Call our Edge Function to verify and process the payment
 */
export async function checkPayment(
  purchaseOption: SubscriptionPlan | GemPackage,
  orderId: string,
  userId?: string
): Promise<CheckPaymentResponse> {
  if (!userId) {
    throw new Error('User ID is required to check payment');
  }

  const { data: responseData, error } = await supabase.functions.invoke('eh-payment-check', {
    body: {
      optionId: purchaseOption.id,
      orderId: orderId,
      provider: 'atlos',
      userId: userId,
      amount: purchaseOption.price,
      isSubscription: 'isSubscription' in purchaseOption && purchaseOption.isSubscription
    }
  }) as { data: CheckPaymentResponse | null, error: Error | null };

  if (error) {
    throw new Error(error.message || 'Failed to process payment');
  }

  if (!responseData?.success) {
    throw new Error(responseData?.error || 'Payment verification failed');
  }

  // Refresh session to get updated metadata
  await supabase.auth.refreshSession();

  return responseData;
}

/**
 * Loads the Atlos script if not already loaded
 */
export function loadAtlosScript(): Promise<boolean> {
  if (window.atlos) {
    return Promise.resolve(true);
  }

  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://atlos.io/packages/app/atlos.js';
    script.async = true;
    script.id = 'atlos-script';

    script.onload = () => {
      if (import.meta.env.DEV) console.log('[DEV] Atlos script loaded');
      resolve(true);
    };

    script.onerror = (e) => {
      if (import.meta.env.DEV) console.error('Atlos script error:', e);
      else console.log('Failed to load Atlos script');

      try {
        document.body.removeChild(script);
      } catch (error) {
        console.error('Error removing Atlos script:', error);
      }

      resolve(false);
    };

    document.body.appendChild(script);
  });
}

/**
 * Get user's email without plus alias for payment providers
 */
export function getPaymentEmail(email?: string): string {
  if (!email) return '';
  return getEmailWithoutAlias(email);
} 